import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { citiesService } from '../../service/cities.service';
import { statesService } from '../../service/state.service';
import { countryService } from '../../service/country.service';
import { roomTypeService } from '../../service/roomType.service';
import { propertyTypeService } from '../../service/propertyType.service';
import { amenityService } from '../../service/amenity.service';
import { facilityService } from '../../service/facilites.service';
import { locationService } from '../../service/location.service';
import {
  createApiArraySliceState,
  createApiArrayReducers,
  CACHE_DURATION,
  isCacheValid,
  ApiArrayState,
} from '../utils/apiSliceUtils';
import { City } from '../../models/cities.model';
import { ListItem } from '../../models/ListItem';

// Types
interface Country {
  id: number;
  name: string;
}

interface State {
  id: number;
  name: string;
  countryId: number;
}

interface PropertyType {
  id: number;
  name: string;
  description?: string;
}

interface RoomType {
  id: number;
  name: string;
  description?: string;
}

interface Amenity {
  id: number;
  name: string;
  description?: string;
}

interface Facility {
  id: number;
  name: string;
  description?: string;
}

interface Location {
  id: number;
  name: string;
  cityId: number;
}

// State interface
interface MasterDataState {
  countries: ApiArrayState<Country>;
  states: ApiArrayState<State>;
  cities: ApiArrayState<City>;
  cityListItems: ApiArrayState<ListItem>;
  propertyTypes: ApiArrayState<PropertyType>;
  roomTypes: ApiArrayState<RoomType>;
  roomTypeListItems: ApiArrayState<ListItem>;
  amenities: ApiArrayState<Amenity>;
  facilities: ApiArrayState<Facility>;
  locations: ApiArrayState<Location>;
}

// Initial state
const initialState: MasterDataState = {
  countries: createApiArraySliceState<Country>(CACHE_DURATION.LONG),
  states: createApiArraySliceState<State>(CACHE_DURATION.LONG),
  cities: createApiArraySliceState<City>(CACHE_DURATION.MEDIUM),
  cityListItems: createApiArraySliceState<ListItem>(CACHE_DURATION.MEDIUM),
  propertyTypes: createApiArraySliceState<PropertyType>(CACHE_DURATION.LONG),
  roomTypes: createApiArraySliceState<RoomType>(CACHE_DURATION.LONG),
  roomTypeListItems: createApiArraySliceState<ListItem>(CACHE_DURATION.LONG),
  amenities: createApiArraySliceState<Amenity>(CACHE_DURATION.MEDIUM),
  facilities: createApiArraySliceState<Facility>(CACHE_DURATION.MEDIUM),
  locations: createApiArraySliceState<Location>(CACHE_DURATION.MEDIUM),
};

// Async thunks with intelligent caching
export const fetchCountries = createAsyncThunk<Country[], void, { rejectValue: string }>(
  'masterData/fetchCountries',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const countriesState = state.masterData.countries;
      
      // Check cache validity
      if (countriesState.data.length > 0 && isCacheValid(countriesState.lastFetch, countriesState.cacheTimeout)) {
        return countriesState.data;
      }
      
      const data = await countryService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch countries');
    }
  }
);

export const fetchStates = createAsyncThunk<State[], number | void, { rejectValue: string }>(
  'masterData/fetchStates',
  async (countryId, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const statesState = state.masterData.states;
      
      // If no countryId provided, check cache for all states
      if (!countryId && statesState.data.length > 0 && isCacheValid(statesState.lastFetch, statesState.cacheTimeout)) {
        return statesState.data;
      }
      
      const data = countryId 
        ? await statesService.getByCountryId(countryId)
        : await statesService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch states');
    }
  }
);

export const fetchCities = createAsyncThunk<City[], number | void, { rejectValue: string }>(
  'masterData/fetchCities',
  async (stateId, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const citiesState = state.masterData.cities;
      
      // If no stateId provided, check cache for all cities
      if (!stateId && citiesState.data.length > 0 && isCacheValid(citiesState.lastFetch, citiesState.cacheTimeout)) {
        return citiesState.data;
      }
      
      const data = stateId 
        ? await citiesService.getByStateId(stateId)
        : await citiesService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch cities');
    }
  }
);

export const fetchCityListItems = createAsyncThunk<ListItem[], void, { rejectValue: string }>(
  'masterData/fetchCityListItems',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const cityListState = state.masterData.cityListItems;
      
      // Check cache validity
      if (cityListState.data.length > 0 && isCacheValid(cityListState.lastFetch, cityListState.cacheTimeout)) {
        return cityListState.data;
      }
      
      const data = await citiesService.getListItems();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch city list items');
    }
  }
);

export const fetchPropertyTypes = createAsyncThunk<PropertyType[], void, { rejectValue: string }>(
  'masterData/fetchPropertyTypes',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const propertyTypesState = state.masterData.propertyTypes;
      
      // Check cache validity
      if (propertyTypesState.data.length > 0 && isCacheValid(propertyTypesState.lastFetch, propertyTypesState.cacheTimeout)) {
        return propertyTypesState.data;
      }
      
      const data = await propertyTypeService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch property types');
    }
  }
);

export const fetchRoomTypes = createAsyncThunk<RoomType[], void, { rejectValue: string }>(
  'masterData/fetchRoomTypes',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const roomTypesState = state.masterData.roomTypes;
      
      // Check cache validity
      if (roomTypesState.data.length > 0 && isCacheValid(roomTypesState.lastFetch, roomTypesState.cacheTimeout)) {
        return roomTypesState.data;
      }
      
      const data = await roomTypeService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch room types');
    }
  }
);

export const fetchRoomTypeListItems = createAsyncThunk<ListItem[], void, { rejectValue: string }>(
  'masterData/fetchRoomTypeListItems',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const roomTypeListState = state.masterData.roomTypeListItems;
      
      // Check cache validity
      if (roomTypeListState.data.length > 0 && isCacheValid(roomTypeListState.lastFetch, roomTypeListState.cacheTimeout)) {
        return roomTypeListState.data;
      }
      
      const data = await roomTypeService.getListItems();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch room type list items');
    }
  }
);

export const fetchAmenities = createAsyncThunk<Amenity[], void, { rejectValue: string }>(
  'masterData/fetchAmenities',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const amenitiesState = state.masterData.amenities;
      
      // Check cache validity
      if (amenitiesState.data.length > 0 && isCacheValid(amenitiesState.lastFetch, amenitiesState.cacheTimeout)) {
        return amenitiesState.data;
      }
      
      const data = await amenityService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch amenities');
    }
  }
);

export const fetchFacilities = createAsyncThunk<Facility[], void, { rejectValue: string }>(
  'masterData/fetchFacilities',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const facilitiesState = state.masterData.facilities;
      
      // Check cache validity
      if (facilitiesState.data.length > 0 && isCacheValid(facilitiesState.lastFetch, facilitiesState.cacheTimeout)) {
        return facilitiesState.data;
      }
      
      const data = await facilityService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch facilities');
    }
  }
);

export const fetchLocations = createAsyncThunk<Location[], number | void, { rejectValue: string }>(
  'masterData/fetchLocations',
  async (cityId, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { masterData: MasterDataState };
      const locationsState = state.masterData.locations;
      
      // If no cityId provided, check cache for all locations
      if (!cityId && locationsState.data.length > 0 && isCacheValid(locationsState.lastFetch, locationsState.cacheTimeout)) {
        return locationsState.data;
      }
      
      const data = cityId 
        ? await locationService.getByCityId(cityId)
        : await locationService.getAll();
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch locations');
    }
  }
);

// Master data slice
const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    // Country reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<Country>()).map(([key, value]) => [
        `countries${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.countries, action),
      ])
    ),
    // State reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<State>()).map(([key, value]) => [
        `states${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.states, action),
      ])
    ),
    // City reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<City>()).map(([key, value]) => [
        `cities${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.cities, action),
      ])
    ),
    // City list items reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<ListItem>()).map(([key, value]) => [
        `cityListItems${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.cityListItems, action),
      ])
    ),
    // Property type reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<PropertyType>()).map(([key, value]) => [
        `propertyTypes${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.propertyTypes, action),
      ])
    ),
    // Room type reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<RoomType>()).map(([key, value]) => [
        `roomTypes${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.roomTypes, action),
      ])
    ),
    // Room type list items reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<ListItem>()).map(([key, value]) => [
        `roomTypeListItems${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.roomTypeListItems, action),
      ])
    ),
    // Amenity reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<Amenity>()).map(([key, value]) => [
        `amenities${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.amenities, action),
      ])
    ),
    // Facility reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<Facility>()).map(([key, value]) => [
        `facilities${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.facilities, action),
      ])
    ),
    // Location reducers
    ...Object.fromEntries(
      Object.entries(createApiArrayReducers<Location>()).map(([key, value]) => [
        `locations${key.charAt(0).toUpperCase() + key.slice(1)}`,
        (state: MasterDataState, action: any) => value(state.locations, action),
      ])
    ),
    // Clear all master data
    clearAllMasterData: (state) => {
      Object.keys(state).forEach(key => {
        const stateKey = key as keyof MasterDataState;
        state[stateKey].data = [];
        state[stateKey].lastFetch = null;
        state[stateKey].error = null;
      });
    },
    // Invalidate all caches
    invalidateAllCaches: (state) => {
      Object.keys(state).forEach(key => {
        const stateKey = key as keyof MasterDataState;
        state[stateKey].lastFetch = null;
      });
    },
  },
  extraReducers: (builder) => {
    // Countries
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.countries.loading = true;
        state.countries.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries.loading = false;
        state.countries.data = action.payload;
        state.countries.lastFetch = Date.now();
        state.countries.error = null;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.countries.loading = false;
        state.countries.error = action.payload || 'Failed to fetch countries';
      });

    // States
    builder
      .addCase(fetchStates.pending, (state) => {
        state.states.loading = true;
        state.states.error = null;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.states.loading = false;
        state.states.data = action.payload;
        state.states.lastFetch = Date.now();
        state.states.error = null;
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.states.loading = false;
        state.states.error = action.payload || 'Failed to fetch states';
      });

    // Cities
    builder
      .addCase(fetchCities.pending, (state) => {
        state.cities.loading = true;
        state.cities.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.cities.loading = false;
        state.cities.data = action.payload;
        state.cities.lastFetch = Date.now();
        state.cities.error = null;
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.cities.loading = false;
        state.cities.error = action.payload || 'Failed to fetch cities';
      });

    // City list items
    builder
      .addCase(fetchCityListItems.pending, (state) => {
        state.cityListItems.loading = true;
        state.cityListItems.error = null;
      })
      .addCase(fetchCityListItems.fulfilled, (state, action) => {
        state.cityListItems.loading = false;
        state.cityListItems.data = action.payload;
        state.cityListItems.lastFetch = Date.now();
        state.cityListItems.error = null;
      })
      .addCase(fetchCityListItems.rejected, (state, action) => {
        state.cityListItems.loading = false;
        state.cityListItems.error = action.payload || 'Failed to fetch city list items';
      });

    // Property types
    builder
      .addCase(fetchPropertyTypes.pending, (state) => {
        state.propertyTypes.loading = true;
        state.propertyTypes.error = null;
      })
      .addCase(fetchPropertyTypes.fulfilled, (state, action) => {
        state.propertyTypes.loading = false;
        state.propertyTypes.data = action.payload;
        state.propertyTypes.lastFetch = Date.now();
        state.propertyTypes.error = null;
      })
      .addCase(fetchPropertyTypes.rejected, (state, action) => {
        state.propertyTypes.loading = false;
        state.propertyTypes.error = action.payload || 'Failed to fetch property types';
      });

    // Room types
    builder
      .addCase(fetchRoomTypes.pending, (state) => {
        state.roomTypes.loading = true;
        state.roomTypes.error = null;
      })
      .addCase(fetchRoomTypes.fulfilled, (state, action) => {
        state.roomTypes.loading = false;
        state.roomTypes.data = action.payload;
        state.roomTypes.lastFetch = Date.now();
        state.roomTypes.error = null;
      })
      .addCase(fetchRoomTypes.rejected, (state, action) => {
        state.roomTypes.loading = false;
        state.roomTypes.error = action.payload || 'Failed to fetch room types';
      });

    // Room type list items
    builder
      .addCase(fetchRoomTypeListItems.pending, (state) => {
        state.roomTypeListItems.loading = true;
        state.roomTypeListItems.error = null;
      })
      .addCase(fetchRoomTypeListItems.fulfilled, (state, action) => {
        state.roomTypeListItems.loading = false;
        state.roomTypeListItems.data = action.payload;
        state.roomTypeListItems.lastFetch = Date.now();
        state.roomTypeListItems.error = null;
      })
      .addCase(fetchRoomTypeListItems.rejected, (state, action) => {
        state.roomTypeListItems.loading = false;
        state.roomTypeListItems.error = action.payload || 'Failed to fetch room type list items';
      });

    // Amenities
    builder
      .addCase(fetchAmenities.pending, (state) => {
        state.amenities.loading = true;
        state.amenities.error = null;
      })
      .addCase(fetchAmenities.fulfilled, (state, action) => {
        state.amenities.loading = false;
        state.amenities.data = action.payload;
        state.amenities.lastFetch = Date.now();
        state.amenities.error = null;
      })
      .addCase(fetchAmenities.rejected, (state, action) => {
        state.amenities.loading = false;
        state.amenities.error = action.payload || 'Failed to fetch amenities';
      });

    // Facilities
    builder
      .addCase(fetchFacilities.pending, (state) => {
        state.facilities.loading = true;
        state.facilities.error = null;
      })
      .addCase(fetchFacilities.fulfilled, (state, action) => {
        state.facilities.loading = false;
        state.facilities.data = action.payload;
        state.facilities.lastFetch = Date.now();
        state.facilities.error = null;
      })
      .addCase(fetchFacilities.rejected, (state, action) => {
        state.facilities.loading = false;
        state.facilities.error = action.payload || 'Failed to fetch facilities';
      });

    // Locations
    builder
      .addCase(fetchLocations.pending, (state) => {
        state.locations.loading = true;
        state.locations.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.locations.loading = false;
        state.locations.data = action.payload;
        state.locations.lastFetch = Date.now();
        state.locations.error = null;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.locations.loading = false;
        state.locations.error = action.payload || 'Failed to fetch locations';
      });
  },
});

export const {
  clearAllMasterData,
  invalidateAllCaches,
} = masterDataSlice.actions;

export default masterDataSlice.reducer;
